import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle, ArrowRight, Globe, Bot, Star, Search, BarChart3, Smartphone, Calendar, Menu, MessageSquare, TrendingUp, Zap, Users, Clock, Shield, Phone, Eye, Languages, Filter, Image, Coffee, Utensils, MapPin, Award, FileText, Monitor } from 'lucide-react';

const Features = () => {
  const handleBookDemo = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const mainFeatures = [
    {
      id: 1,
      title: "Website Development",
      icon: Globe,
      description: "Mobile-First Architecture",
      subtitle: "Stunning on every device. Optimized for fast loading on all connections. Built for conversions, not just aesthetics.",
      features: [
        {
          title: "Smart Menu System",
          icon: Menu,
          items: [
            "Searchable, filterable menu galleries",
            "Dietary filters (vegan, gluten-free, etc.)",
            "High-quality food photography optimization"
          ]
        },
        {
          title: "Booking Integration",
          icon: Calendar,
          items: [
            "Direct connection to OpenTable, Resy, Yelp Reservations",
            "Toast, SevenRooms, and all major platforms",
            "One-click reservations"
          ]
        },
        {
          title: "Built to Convert",
          icon: TrendingUp,
          items: [
            "Strategic placement of CTAs, social proof, and urgency triggers",
            "Every element tested for maximum bookings"
          ]
        }
      ]
    },
    {
      id: 2,
      title: "Isabella AI Assistant",
      icon: Bot,
      description: "Brand-Trained Intelligence",
      subtitle: "Isabella learns your menu, your voice, your policies. She sounds like your team, not a robot.",
      features: [
        {
          title: "Never Sleeps",
          icon: Clock,
          items: [
            "Captures reservations at 2 AM, 6 AM, holidays, busy Friday nights",
            "Zero missed opportunities"
          ]
        },
        {
          title: "Instant Responses",
          icon: Zap,
          items: [
            "Answers questions in 3 seconds",
            "No hold music. No 'let me check and call you back'"
          ]
        },
        {
          title: "Multilingual Communication",
          icon: Languages,
          items: [
            "Seamlessly switches between English, Spanish, French, Hindi, Punjabi, and Mandarin",
            "Based on customer preference"
          ]
        },
        {
          title: "Smart Escalation",
          icon: Users,
          items: [
            "Complex situations route to your team with full context",
            "No frustrated customers repeating themselves"
          ]
        },
        {
          title: "Integration Intelligence",
          icon: Calendar,
          items: [
            "Checks real-time availability with your booking system",
            "Confirms reservations instantly. Sends confirmation texts"
          ]
        },
        {
          title: "Continuous Learning",
          icon: BarChart3,
          items: [
            "Gets smarter every week. Learns from every conversation",
            "Improves based on your feedback"
          ]
        }
      ]
    },
    {
      id: 3,
      title: "Review Accelerator",
      icon: Star,
      description: "Smart Review Management",
      subtitle: "Automated outreach, AI responses, and reputation monitoring to boost your online presence and review volume.",
      features: [
        {
          title: "Automated Outreach",
          icon: Clock,
          items: [
            "SMS sent 2 hours after dining. Perfectly timed when experience is fresh."
          ]
        },
        {
          title: "AI Response Writer",
          icon: MessageSquare,
          items: [
            "Automatically generates thoughtful, on-brand responses to all reviews"
          ]
        },
        {
          title: "Reputation Monitoring",
          icon: Eye,
          items: [
            "Real-time tracking across Google",
            "Instant alerts for new reviews"
          ]
        },
        {
          title: "Negative Review Management",
          icon: Shield,
          items: [
            "Automated professional responses to negative reviews",
            "Maintains your reputation 24/7"
          ]
        },
        {
          title: "Competitive Analysis",
          icon: Award,
          items: [
            "Track competitor ratings and review velocity",
            "See what customers love about them—and where they're weak"
          ]
        }
      ]
    },
    {
      id: 4,
      title: "Local SEO Engine",
      icon: Search,
      description: "Google Business Profile Mastery",
      subtitle: "Professional photos, strategic posts, Q&A optimization, category selection, attribute maximization.",
      features: [
        {
          title: "Neighborhood Content Strategy",
          icon: MapPin,
          items: [
            "Location-specific landing pages",
            "'Best [cuisine] in [neighborhood]' content",
            "Local event tie-ins"
          ]
        },
        {
          title: "Citation Building",
          icon: FileText,
          items: [
            "Consistent NAP (name, address, phone) across 50+ directories",
            "Yelp, TripAdvisor, OpenTable, Zomato, and more"
          ]
        },
        {
          title: "Competitor Intelligence",
          icon: TrendingUp,
          items: [
            "Monthly reports showing your ranking vs. top 5 competitors",
            "Identify gaps and opportunities"
          ]
        },
        {
          title: "Technical SEO",
          icon: Globe,
          items: [
            "Schema markup for restaurants. Menu markup. Review markup",
            "Local business structured data"
          ]
        },
        {
          title: "Link Building",
          icon: Users,
          items: [
            "Strategic partnerships with local food bloggers",
            "Tourism sites, and community organizations"
          ]
        }
      ]
    },
    {
      id: 5,
      title: "Growth Command Center",
      icon: BarChart3,
      description: "Single Dashboard for Everything",
      subtitle: "Traffic Analytics, Booking Intelligence, Review Performance, Isabella Insights, Automated Reporting.",
      features: [
        {
          title: "Traffic Analytics",
          icon: TrendingUp,
          items: [
            "Visitors, sources, popular pages, conversion paths",
            "See what's driving reservations"
          ]
        },
        {
          title: "Booking Intelligence",
          icon: Calendar,
          items: [
            "Reservations by source (Isabella, website, phone, Google)",
            "Peak booking times. Revenue attribution"
          ]
        },
        {
          title: "Review Performance",
          icon: Star,
          items: [
            "New reviews, rating trends, response rates, sentiment analysis",
            "Competitive benchmarking"
          ]
        },
        {
          title: "Isabella Insights",
          icon: Bot,
          items: [
            "Conversation volume, common questions, booking conversion rate",
            "Multilingual usage"
          ]
        },
        {
          title: "Automated Reporting",
          icon: FileText,
          items: [
            "Weekly email summaries. Monthly deep-dive reports",
            "Custom reports on demand"
          ]
        }
      ]
    }
  ];

  return (
    <div className="pt-16 overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen bg-gradient-to-br from-brand-charcoal via-brand-charcoal-700 to-brand-charcoal">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-br from-brand-teal/20 to-brand-teal-600/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 left-20 w-64 h-64 bg-gradient-to-br from-brand-sienna/20 to-brand-gold/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-gradient-to-br from-[#2D6A4F]/20 to-[#2D6A4F]/20 rounded-full blur-3xl animate-pulse delay-500"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32 text-center">
          <Badge className="inline-block mb-8 bg-white/10 backdrop-blur-md border border-white/20 text-white px-8 py-4 text-lg font-medium">
            🚀 Restaurant Growth Platform
          </Badge>
          <h1 className="text-5xl lg:text-7xl font-bold text-white mb-8 leading-tight">
            The Complete Restaurant
            <span className="block text-[#2D6A4F]">
              Growth System
            </span>
            <span className="block text-3xl lg:text-4xl text-slate-300 font-medium mt-4">
              Every Tool You Need
            </span>
          </h1>
          <p className="text-xl text-slate-300 max-w-4xl mx-auto mb-12 leading-relaxed">
            From AI-powered reservations to automated review management—discover the 5 core systems that transform struggling restaurants into industry leaders.
          </p>
          
          {/* Feature preview cards */}
          <div className="flex justify-center items-center space-x-4 mb-16">
            {[
              { icon: Globe, color: "from-cyan-500 to-blue-500", name: "Website" },
              { icon: Bot, color: "from-purple-500 to-pink-500", name: "Isabella AI" },
              { icon: Star, color: "from-[#2D6A4F] to-[#2D6A4F]", name: "Reviews" },
              { icon: Search, color: "from-orange-500 to-red-500", name: "Local SEO" },
              { icon: BarChart3, color: "from-indigo-500 to-blue-500", name: "Analytics" }
            ].map((item, index) => (
              <div key={index} className="group cursor-pointer">
                <div className={`w-16 h-16 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg backdrop-blur-sm border border-white/20 animate-float`} style={{animationDelay: `${index * 200}ms`}}>
                  <item.icon className="text-white" size={24} />
                </div>
                <div className="text-white/80 text-sm mt-2 group-hover:text-white transition-colors duration-300">{item.name}</div>
              </div>
            ))}
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto">
            {[
              { number: "50+", label: "Restaurants Growing", icon: Users },
              { number: "340%", label: "Avg Booking Increase", icon: TrendingUp },
              { number: "24/7", label: "AI Assistant Active", icon: Clock }
            ].map((stat, index) => (
              <div key={index} className="text-center group">
                <div className="w-12 h-12 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:bg-white/20 transition-all duration-300">
                  <stat.icon className="text-[#2D6A4F]" size={20} />
                </div>
                <div className="text-3xl font-bold text-white mb-1">{stat.number}</div>
                <div className="text-slate-300 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Features */}
      {mainFeatures.map((feature, index) => (
        <section key={feature.id} className={`py-32 ${index % 2 === 0 ? 'bg-white' : 'bg-gradient-to-br from-slate-50 to-slate-100'} relative overflow-hidden`}>
          {/* Background decorative elements */}
          <div className="absolute inset-0">
            <div className={`absolute ${index % 2 === 0 ? 'top-0 right-0' : 'bottom-0 left-0'} w-96 h-96 opacity-10`}>
              <feature.icon size={384} className="text-slate-300" />
            </div>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Feature Header */}
            <div className="text-center mb-20">
              <div className={`inline-flex items-center space-x-3 bg-gradient-to-r ${
                index === 0 ? 'from-cyan-500/10 to-blue-500/10 border-cyan-400/20' :
                index === 1 ? 'from-purple-500/10 to-pink-500/10 border-purple-400/20' :
                index === 2 ? 'from-[#2D6A4F]/10 to-[#2D6A4F]/10 border-[#2D6A4F]/20' :
                index === 3 ? 'from-orange-500/10 to-red-500/10 border-orange-400/20' :
                'from-indigo-500/10 to-blue-500/10 border-indigo-400/20'
              } backdrop-blur-md rounded-full px-6 py-3 border mb-8`}>
                <feature.icon className={`${
                  index === 0 ? 'text-cyan-500' :
                  index === 1 ? 'text-purple-500' :
                  index === 2 ? 'text-[#2D6A4F]' :
                  index === 3 ? 'text-orange-500' :
                  'text-indigo-500'
                }`} size={24} />
                <span className={`font-semibold ${
                  index === 0 ? 'text-cyan-600' :
                  index === 1 ? 'text-purple-600' :
                  index === 2 ? 'text-[#2D6A4F]' :
                  index === 3 ? 'text-orange-600' :
                  'text-indigo-600'
                }`}>{feature.title}</span>
              </div>
              
              <h2 className="text-4xl lg:text-6xl font-bold text-slate-900 mb-6">
                {feature.description}
              </h2>
              
              <p className="text-xl text-slate-600 max-w-4xl mx-auto leading-relaxed">
                {feature.subtitle}
              </p>
            </div>

            {/* Feature Details */}
            <div className={`grid ${feature.features.length >= 4 ? 'lg:grid-cols-3' : 'lg:grid-cols-2'} gap-8`}>
              {feature.features.map((subFeature, subIndex) => (
                <Card key={subIndex} className="group border-0 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 bg-white/80 backdrop-blur-sm">
                  <CardHeader className="pb-6">
                    <div className="flex items-center space-x-4 mb-4">
                      <div className={`w-16 h-16 bg-gradient-to-r ${
                        index === 0 ? 'from-cyan-500 to-blue-500' :
                        index === 1 ? 'from-purple-500 to-pink-500' :
                        index === 2 ? 'from-[#2D6A4F] to-[#2D6A4F]' :
                        index === 3 ? 'from-orange-500 to-red-500' :
                        'from-indigo-500 to-blue-500'
                      } rounded-2xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg`}>
                        <subFeature.icon className="text-white" size={28} />
                      </div>
                      <CardTitle className="text-2xl text-slate-900 group-hover:bg-gradient-to-r group-hover:from-slate-700 group-hover:to-slate-900 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">
                        {subFeature.title}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  
                  <CardContent>
                    <ul className="space-y-4">
                      {subFeature.items.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-start space-x-3 group-hover:translate-x-2 transition-transform duration-300" style={{transitionDelay: `${itemIndex * 100}ms`}}>
                          <div className="w-6 h-6 bg-gradient-to-r from-[#2D6A4F] to-[#2D6A4F] rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                            <CheckCircle className="text-white" size={14} />
                          </div>
                          <span className="text-slate-700 group-hover:text-slate-900 transition-colors duration-300 leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* What Isabella Does Better Than Humans Section */}
      <section className="py-32 bg-gradient-to-br from-brand-charcoal via-brand-charcoal-700 to-brand-charcoal relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-gradient-to-br from-brand-sienna/20 to-brand-gold/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-gradient-to-br from-brand-teal/20 to-brand-teal-600/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-6xl font-bold text-white mb-8">
            What Isabella Does 
            <span className="block bg-gradient-to-r from-brand-sienna to-brand-gold bg-clip-text text-transparent">
              Better Than Humans
            </span>
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-16">
            {[
              { title: "Never Sleeps", desc: "Captures reservations at 2 AM, 6 AM, holidays, busy Friday nights", icon: Clock },
              { title: "Instant Responses", desc: "Answers questions in 3 seconds. No hold music", icon: Zap },
              { title: "Multilingual", desc: "Seamlessly switches between 6 languages", icon: Languages },
              { title: "Smart Escalation", desc: "Routes complex situations with full context", icon: Users },
              { title: "Integration Intelligence", desc: "Checks real-time availability instantly", icon: Calendar },
              { title: "Continuous Learning", desc: "Gets smarter every week", icon: BarChart3 }
            ].map((item, index) => (
              <Card key={index} className="bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 transition-all duration-500 transform hover:-translate-y-4">
                <CardHeader className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-brand-sienna to-brand-sienna-600 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-500 shadow-lg">
                    <item.icon className="text-white" size={28} />
                  </div>
                  <CardTitle className="text-xl text-white mb-4">{item.title}</CardTitle>
                  <CardDescription className="text-brand-ivory/80">{item.desc}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Ecosystem */}
      <section className="py-32 bg-gradient-to-r from-brand-charcoal to-brand-charcoal-700 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-32 h-32 bg-gradient-to-br from-brand-teal/20 to-brand-teal-600/20 rounded-full blur-2xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-32 h-32 bg-gradient-to-br from-brand-sienna/20 to-brand-gold/20 rounded-full blur-2xl animate-pulse delay-1000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl lg:text-6xl font-bold text-white mb-8">
              Seamlessly Integrates With
              <span className="block text-[#2D6A4F]">
                Everything You Already Use
              </span>
            </h2>
            <p className="text-xl text-[#F9F7F4] max-w-3xl mx-auto">
              No disruption to your current workflow. PrismoSpark connects with all major restaurant platforms and tools.
            </p>
          </div>

          {/* Integration Grid */}
          <div className="grid md:grid-cols-4 gap-8 mb-20">
            {[
              { category: "Booking Platforms", items: ["OpenTable", "Resy", "Yelp Reservations", "SevenRooms"], icon: Calendar, color: "from-brand-teal to-brand-teal-600" },
              { category: "POS Systems", items: ["Toast", "Square", "Clover", "Lightspeed"], icon: Monitor, color: "from-brand-sienna to-brand-sienna-600" },
              { category: "Review Platforms", items: ["Google", "Yelp", "TripAdvisor", "Facebook"], icon: Star, color: "from-brand-gold to-brand-gold-600" },
              { category: "Marketing Tools", items: ["Mailchimp", "Instagram", "Facebook Ads", "Google Ads"], icon: MessageSquare, color: "from-brand-forest to-brand-forest-600" }
            ].map((category, index) => (
              <Card key={index} className="bg-white/5 backdrop-blur-md border border-white/10 hover:bg-white/10 transition-all duration-500 group">
                <CardHeader className="text-center">
                  <div className={`w-16 h-16 bg-gradient-to-br ${category.color} rounded-2xl flex items-center justify-center mx-auto mb-4 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg`}>
                    <category.icon className="text-white" size={28} />
                  </div>
                  <CardTitle className="text-white text-lg mb-4">{category.category}</CardTitle>
                  <div className="space-y-2">
                    {category.items.map((item, itemIndex) => (
                      <div key={itemIndex} className="text-brand-ivory/80 text-sm group-hover:text-white transition-colors duration-300">
                        {item}
                      </div>
                    ))}
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>

          {/* Success Metrics */}
          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-12">
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold text-white mb-4">Real Results From Real Restaurants</h3>
              <p className="text-[#F9F7F4]">These aren't promises—they're proven outcomes from restaurants using PrismoSpark.</p>
            </div>
            
            <div className="grid md:grid-cols-4 gap-8">
              {[
                { metric: "340%", description: "Average increase in online reservations within 90 days", icon: TrendingUp },
                { metric: "1.5★", description: "Average review rating improvement across all platforms", icon: Star },
                { metric: "15hrs", description: "Weekly staff time saved through AI automation", icon: Clock },
                { metric: "24/7", description: "Customer service availability with zero missed calls", icon: Bot }
              ].map((result, index) => (
                <div key={index} className="text-center group">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#2D6A4F] to-[#2D6A4F] rounded-2xl flex items-center justify-center mx-auto mb-4 transform group-hover:scale-110 transition-all duration-300 shadow-lg">
                    <result.icon className="text-white" size={28} />
                  </div>
                  <div className="text-4xl font-bold text-white mb-2 group-hover:bg-gradient-to-r group-hover:from-[#2D6A4F] group-hover:to-[#2D6A4F] group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">
                    {result.metric}
                  </div>
                  <div className="text-[#F9F7F4] text-sm leading-relaxed">{result.description}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-gradient-to-br from-brand-charcoal via-brand-charcoal-700 to-brand-charcoal relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-brand-teal/5 to-transparent"></div>
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-brand-teal/10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-brand-sienna/8 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl lg:text-6xl font-bold text-white mb-8">
            Ready to Transform
            <span className="block text-[#2D6A4F]">
              Your Restaurant?
            </span>
          </h2>
          <p className="text-xl text-[#F9F7F4] mb-12 leading-relaxed">
            See exactly how these features work together to create a complete growth system for your restaurant. Book your personalized demo today.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <Button 
              onClick={handleBookDemo}
              className="group relative bg-[#D4623C] hover:bg-[#D4623C] text-white font-bold text-xl px-12 py-6 rounded-2xl shadow-2xl shadow-[#D4623C]/25 hover:shadow-[#D4623C]/40 transition-all duration-300 transform hover:-translate-y-1 hover:scale-[1.02] border-0 overflow-hidden"
            >
              {/* Animated background layer */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              {/* Shimmer effect */}
              <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 ease-in-out"></div>
              
              {/* Content */}
              <div className="relative z-10 flex items-center justify-center space-x-3">
                <Monitor className="w-6 h-6 group-hover:animate-pulse" />
                <span>Book Your Demo</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </div>
              
              {/* Glow effect */}
              <div className="absolute inset-0 rounded-2xl bg-[#D4623C]/50 blur-xl opacity-0 group-hover:opacity-70 transition-opacity duration-500 -z-10 scale-110"></div>
            </Button>
            
            <div className="text-[#F9F7F4] text-center">
              <div className="text-sm mb-2">⚡ 15-minute setup call</div>
              <div className="text-sm">🎯 Customized to your restaurant type</div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section Placeholder for Scroll Target */}
      <section id="contact" className="py-20 bg-gradient-to-br from-brand-ivory-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-5xl font-bold text-brand-charcoal mb-8">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-brand-charcoal-600 mb-8 leading-relaxed">
            Contact us to book your demo and see these features in action.
          </p>
          <div className="bg-white p-8 rounded-2xl shadow-2xl border border-brand-charcoal/20">
            <p className="text-lg text-brand-charcoal-600 mb-4">
              📞 Phone: <span className="font-semibold">(555) 123-GROW</span>
            </p>
            <p className="text-lg text-brand-charcoal-600 mb-4">
              ✉️ Email: <span className="font-semibold">hello@prismospark.com</span>
            </p>
            <p className="text-base text-brand-charcoal-500">
              Our team is available Monday-Friday, 9 AM - 6 PM EST
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Features;